import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { VehicleModal } from '../components/VehicleModal';
import { CustomerModal } from '../components/CustomerModal';
import { SaleModal } from '../components/SaleModal';
import { SalesChart } from '../components/SalesChart';
import { vehicleService, customerService, saleService } from '../services/api';
import { 
  Car, 
  Users, 
  TrendingUp, 
  DollarSign, 
  Settings, 
  LogOut, 
  Bell, 
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Check,
  AlertCircle,
  X,
  ChevronUp,
  ChevronDown
} from 'lucide-react';

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [vehicles, setVehicles] = useState([]);
  const [vehicleTotal, setVehicleTotal] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [viewingVehicle, setViewingVehicle] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [showDeleteCustomerConfirm, setShowDeleteCustomerConfirm] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [editingSale, setEditingSale] = useState(null);
  const [viewingSale, setViewingSale] = useState(null);
  const [showDeleteSaleConfirm, setShowDeleteSaleConfirm] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [notification, setNotification] = useState(null);
  const [vehicleStats, setVehicleStats] = useState(null);
  const [saleStats, setSaleStats] = useState(null);
  const [customerStats, setCustomerStats] = useState(null);

  // Função para recarregar todos os dados da dashboard
  const reloadDashboardData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      // Carregar veículos (trazer todos para o painel admin)
      const vehiclesResponse = await vehicleService.getAllVehicles({ limit: 1000 });
      if (vehiclesResponse && vehiclesResponse.success) {
        const data = vehiclesResponse.data || {};
        setVehicles(sortVehiclesByDisplayOrder(data.vehicles || []));
        setVehicleTotal(typeof data.total === 'number' ? data.total : (data.vehicles || []).length);
      } else {
        console.error('❌ Erro ao carregar veículos:', vehiclesResponse?.message || 'Resposta inválida');
        setVehicles([]);
        setVehicleTotal(0);
      }

      // Estatísticas de veículos
      try {
        const vStatsResponse = await vehicleService.getStatistics();
        if (vStatsResponse && vStatsResponse.success) {
          setVehicleStats(vStatsResponse.data);
        } else {
          setVehicleStats(null);
        }
      } catch (err) {
        console.error('❌ Erro ao carregar estatísticas de veículos:', err);
        setVehicleStats(null);
      }

      // Carregar clientes
      const customersResponse = await customerService.getAllCustomers();
      if (customersResponse && customersResponse.success) {
        setCustomers(customersResponse.data.customers || []);
      } else {
        console.error('❌ Erro ao carregar clientes:', customersResponse?.message || 'Resposta inválida');
        setCustomers([]);
      }

      // Carregar vendas
      const salesResponse = await saleService.getAllSales();
      if (salesResponse && salesResponse.success) {
        setSales(salesResponse.data.sales || []);
      } else {
        console.error('❌ Erro ao carregar vendas:', salesResponse?.message || 'Resposta inválida');
        setSales([]);
      }

      // Estatísticas de vendas
      try {
        const sStatsResponse = await saleService.getSaleStatistics();
        if (sStatsResponse && sStatsResponse.success) {
          setSaleStats(sStatsResponse.data);
        } else {
          setSaleStats(null);
        }
      } catch (err) {
        console.error('❌ Erro ao carregar estatísticas de vendas:', err);
        setSaleStats(null);
      }

      // Estatísticas de clientes
      try {
        const cStatsResponse = await customerService.getCustomerStatistics();
        if (cStatsResponse && cStatsResponse.success) {
          setCustomerStats(cStatsResponse.data);
        } else {
          setCustomerStats(null);
        }
      } catch (err) {
        console.error('❌ Erro ao carregar estatísticas de clientes:', err);
        setCustomerStats(null);
      }
    } catch (error) {
      console.error('❌ Erro ao recarregar dados:', error);
      console.error('❌ Stack trace:', error.stack);
      
      // Em caso de erro, definir arrays vazios para evitar tela branca
      setVehicles([]);
      setVehicleTotal(0);
      setCustomers([]);
      setSales([]);
      setVehicleStats(null);
      setSaleStats(null);
      setCustomerStats(null);
    } finally {
      if (showLoading) {
        // Garantir que o loading seja sempre finalizado
        setLoading(false);
      }
    }
  };

  // Carregar dados da API
  useEffect(() => {
    reloadDashboardData();
    
    // Timeout de segurança para garantir que o loading seja finalizado
    const safetyTimeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 10000); // 10 segundos
    
    return () => clearTimeout(safetyTimeout);
  }, []);

  const getMonthKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const getSeriesValue = (series, monthKey, field = 'count') => {
    if (!series || !Array.isArray(series)) return 0;
    const item = series.find((entry) => entry.month === monthKey);
    if (!item) return 0;
    const value = item[field];
    return typeof value === 'number' ? value : parseFloat(value || 0) || 0;
  };

  const computeChange = (current, previous) => {
    if (!previous || previous <= 0) {
      if (!current || current <= 0) {
        return { value: '0%', type: 'positive' };
      }
      return { value: '+100%', type: 'positive' };
    }
    const diff = current - previous;
    const pct = (diff / previous) * 100;
    const rounded = Math.round(pct);
    const sign = rounded > 0 ? '+' : '';
    return {
      value: `${sign}${rounded}%`,
      type: rounded >= 0 ? 'positive' : 'negative'
    };
  };

  const now = new Date();
  const currentMonthKey = getMonthKey(now);
  const prevMonthKey = getMonthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1));

  const vehiclesCurrentMonth = getSeriesValue(vehicleStats?.vehiclesByMonth, currentMonthKey);
  const vehiclesPrevMonth = getSeriesValue(vehicleStats?.vehiclesByMonth, prevMonthKey);
  const vehiclesChange = computeChange(vehiclesCurrentMonth, vehiclesPrevMonth);

  const salesCurrentMonth = getSeriesValue(saleStats?.salesByMonth, currentMonthKey, 'count');
  const salesPrevMonth = getSeriesValue(saleStats?.salesByMonth, prevMonthKey, 'count');
  const salesChange = computeChange(salesCurrentMonth, salesPrevMonth);

  const revenueCurrentMonth = getSeriesValue(saleStats?.salesByMonth, currentMonthKey, 'revenue');
  const revenuePrevMonth = getSeriesValue(saleStats?.salesByMonth, prevMonthKey, 'revenue');
  const revenueChange = computeChange(revenueCurrentMonth, revenuePrevMonth);

  const customersCurrentMonth = getSeriesValue(customerStats?.customersByMonth, currentMonthKey);
  const customersPrevMonth = getSeriesValue(customerStats?.customersByMonth, prevMonthKey);
  const customersChange = computeChange(customersCurrentMonth, customersPrevMonth);

  const activeCustomersTotal = customerStats?.activeCustomers ?? customers.filter(customer => customer.is_active).length;

  const stats = [
    {
      title: 'Total de Veículos',
      value: vehicleTotal.toString(),
      change: vehiclesChange.value,
      changeType: vehiclesChange.type,
      icon: <Car className="h-6 w-6" />,
      color: 'bg-blue-500'
    },
    {
      title: 'Vendidos Este Mês',
      value: salesCurrentMonth.toString(),
      change: salesChange.value,
      changeType: salesChange.type,
      icon: <TrendingUp className="h-6 w-6" />,
      color: 'bg-green-500'
    },
    {
      title: 'Faturamento',
      value: new Intl.NumberFormat('pt-BR', { 
        style: 'currency', 
        currency: 'BRL',
        notation: 'compact'
      }).format(revenueCurrentMonth),
      change: revenueChange.value,
      changeType: revenueChange.type,
      icon: <DollarSign className="h-6 w-6" />,
      color: 'bg-yellow-500'
    },
    {
      title: 'Clientes Ativos',
      value: activeCustomersTotal.toString(),
      change: customersChange.value,
      changeType: customersChange.type,
      icon: <Users className="h-6 w-6" />,
      color: 'bg-purple-500'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'disponivel':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'vendido':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'reservado':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'disponivel':
        return 'Disponível';
      case 'vendido':
        return 'Vendido';
      case 'reservado':
        return 'Reservado';
      default:
        return 'Indisponível';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const sortVehiclesByDisplayOrder = (list) => {
    return [...list].sort((a, b) => {
      const orderA = typeof a.display_order === 'number' ? a.display_order : 0;
      const orderB = typeof b.display_order === 'number' ? b.display_order : 0;
      if (orderA !== orderB) return orderA - orderB;
      return (b.id || 0) - (a.id || 0);
    });
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(d.getTime())) return '';

    const diffMs = Date.now() - d.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'Agora mesmo';
    if (diffMinutes < 60) return `${diffMinutes} min atrás`;
    if (diffHours < 24) return `${diffHours} horas atrás`;
    return `${diffDays} dias atrás`;
  };

  const recentActivities = (() => {
    const activities = [];

    // Veículos criados recentemente
    vehicles.forEach((vehicle) => {
      const createdAt = vehicle.createdAt || vehicle.created_at;
      if (!createdAt) return;

      activities.push({
        type: 'vehicle',
        title: 'Novo veículo cadastrado',
        detail: `${vehicle.brand} ${vehicle.model} ${vehicle.year}`,
        date: new Date(createdAt)
      });
    });

    // Vendas realizadas
    sales.forEach((sale) => {
      const createdAt = sale.created_at || sale.sale_date;
      if (!createdAt) return;

      activities.push({
        type: 'sale',
        title: 'Venda realizada',
        detail: `${sale.vehicle?.brand || ''} ${sale.vehicle?.model || ''} ${sale.vehicle?.year || ''}`.trim(),
        date: new Date(createdAt)
      });
    });

    // Clientes cadastrados
    customers.forEach((customer) => {
      const createdAt = customer.created_at;
      if (!createdAt) return;

      activities.push({
        type: 'customer',
        title: 'Cliente cadastrado',
        detail: customer.name,
        date: new Date(createdAt)
      });
    });

    return activities
      .filter((a) => a.date && !Number.isNaN(a.date.getTime()))
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 10);
  })();

  const handleVehicleSubmit = async (vehicleData) => {
    try {
      // vehicleData vem do VehicleModal e normalmente é um FormData
      let payload = vehicleData;

      if (typeof FormData !== 'undefined' && vehicleData instanceof FormData) {
        // Adicionar o usuário criador ao FormData
        payload.append('created_by', user.id);
      } else {
        // Fallback para caso vehicleData venha como objeto simples
        payload = {
          ...vehicleData,
          created_by: user.id
        };
      }

      // Chamar API para criar veículo
      const response = await vehicleService.createVehicle(payload);
      
      if (response.success) {
        showNotification('Veículo cadastrado com sucesso!');
        
        // Atualizar lista local imediatamente
        setVehicles(prev => sortVehiclesByDisplayOrder([response.data, ...prev]));
        
        // Recarregar dados da dashboard via AJAX em background
        setTimeout(() => {
          reloadDashboardData(false);
        }, 1000);
      } else {
        throw new Error(response.message || 'Erro ao cadastrar veículo');
      }
      
    } catch (error) {
      console.error('Erro ao cadastrar veículo:', error);
      throw error;
    }
  };

  const handleCustomerSubmit = async (customerData) => {
    try {
      // Preparar dados para envio
      const customerToCreate = {
        ...customerData,
        created_by: user.id // ID do usuário logado
      };

      // Chamar API para criar cliente
      const response = await customerService.createCustomer(customerToCreate);
      
      if (response.success) {
        // Recarregar dados da dashboard via AJAX
        await reloadDashboardData(false);
      } else {
        throw new Error(response.message || 'Erro ao cadastrar cliente');
      }
      
    } catch (error) {
      console.error('Erro ao cadastrar cliente:', error);
      throw error;
    }
  };

  const handleSaleSubmit = async (saleData) => {
    try {
      // Preparar dados para envio
      const saleToCreate = {
        ...saleData,
        created_by: user.id // ID do usuário logado
      };

      // Chamar API para criar venda
      const response = await saleService.createSale(saleToCreate);
      
      if (response.success) {
        // Recarregar dados da dashboard via AJAX
        await reloadDashboardData(false);
      } else {
        throw new Error(response.message || 'Erro ao registrar venda');
      }
      
    } catch (error) {
      console.error('Erro ao registrar venda:', error);
      throw error;
    }
  };

  // Função para editar veículo
  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowVehicleModal(true);
  };

  // Função para visualizar veículo
  const handleViewVehicle = (vehicle) => {
    setViewingVehicle(vehicle);
  };

  // Função para excluir veículo
  const handleDeleteVehicle = (vehicle) => {
    setVehicleToDelete(vehicle);
    setShowDeleteConfirm(true);
  };

  // Função para confirmar exclusão
  const confirmDeleteVehicle = async () => {
    try {
      const response = await vehicleService.deleteVehicle(vehicleToDelete.id);
      
      if (response.success) {
        showNotification('Veículo excluído com sucesso!');
        
        // Atualizar lista local imediatamente
        setVehicles(prev => prev.filter(v => v.id !== vehicleToDelete.id));
        
        // Fechar modal de confirmação
        setShowDeleteConfirm(false);
        setVehicleToDelete(null);
        
        // Recarregar dados da dashboard via AJAX em background
        setTimeout(() => {
          reloadDashboardData(false);
        }, 1000);
      } else {
        throw new Error(response.message || 'Erro ao excluir veículo');
      }
    } catch (error) {
      console.error('❌ Erro ao excluir veículo:', error);
      showNotification('Erro ao excluir veículo: ' + error.message, 'error');
      setShowDeleteConfirm(false);
      setVehicleToDelete(null);
    }
  };

  // Função para atualizar veículo
  const handleUpdateVehicle = async (vehicleData) => {
    try {
      const response = await vehicleService.updateVehicle(editingVehicle.id, vehicleData);
      
      if (response.success) {
        showNotification('Veículo atualizado com sucesso!');
        
        // Atualizar lista local imediatamente
        setVehicles(prev => {
          const updated = prev.map(v => 
            v.id === editingVehicle.id ? { ...v, ...response.data } : v
          );
          return sortVehiclesByDisplayOrder(updated);
        });

        // Recarregar dados da dashboard via AJAX em background
        setTimeout(() => {
          reloadDashboardData(false);
        }, 1000);
      } else {
        throw new Error(response.message || 'Erro ao atualizar veículo');
      }
    } catch (error) {
      console.error('Erro ao atualizar veículo:', error);
      throw error;
    }
  };

  // Função para fechar modal de veículo
  const handleCloseVehicleModal = () => {
    setShowVehicleModal(false);
    setEditingVehicle(null);
  };

  const handleChangeDisplayOrder = async (vehicle, direction) => {
    if (!vehicle) return;

    const currentOrder = typeof vehicle.display_order === 'number' ? vehicle.display_order : 0;
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;

    try {
      const response = await vehicleService.updateVehicleDisplayOrder(vehicle.id, newOrder);

      if (response.success) {
        setVehicles(prev => {
          const updated = prev.map(v =>
            v.id === vehicle.id ? { ...v, ...response.data } : v
          );
          return sortVehiclesByDisplayOrder(updated);
        });
      }
    } catch (error) {
      console.error('Erro ao atualizar display_order do veículo:', error);
      showNotification('Erro ao atualizar posição do veículo', 'error');
    }
  };

  // Funções para CRUD de Clientes
  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setShowCustomerModal(true);
  };

  const handleViewCustomer = (customer) => {
    setViewingCustomer(customer);
  };

  const handleDeleteCustomer = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteCustomerConfirm(true);
  };

  const confirmDeleteCustomer = async () => {
    try {
      const response = await customerService.deleteCustomer(customerToDelete.id);
      
      if (response.success) {
        showNotification('Cliente excluído com sucesso!');
        
        // Atualizar lista local imediatamente
        setCustomers(prev => prev.filter(c => c.id !== customerToDelete.id));
        
        // Recarregar dados da dashboard via AJAX em background
        setTimeout(() => {
          reloadDashboardData(false);
        }, 1000);
      } else {
        throw new Error(response.message || 'Erro ao excluir cliente');
      }
    } catch (error) {
      console.error('❌ Erro ao excluir cliente:', error);
      showNotification('Erro ao excluir cliente: ' + error.message, 'error');
    } finally {
      setShowDeleteCustomerConfirm(false);
      setCustomerToDelete(null);
    }
  };

  const handleUpdateCustomer = async (customerData) => {
    try {
      const response = await customerService.updateCustomer(editingCustomer.id, customerData);
      
      if (response.success) {
        showNotification('Cliente atualizado com sucesso!');
        
        // Recarregar dados da dashboard via AJAX
        await reloadDashboardData(false);
      } else {
        throw new Error(response.message || 'Erro ao atualizar cliente');
      }
    } catch (error) {
      console.error('Erro ao atualizar cliente:', error);
      throw error;
    }
  };

  const handleCloseCustomerModal = () => {
    setShowCustomerModal(false);
    setEditingCustomer(null);
  };

  // Funções para CRUD de Vendas
  const handleEditSale = (sale) => {
    setEditingSale(sale);
    setShowSaleModal(true);
  };

  const handleViewSale = (sale) => {
    setViewingSale(sale);
  };

  const handleDeleteSale = (sale) => {
    setSaleToDelete(sale);
    setShowDeleteSaleConfirm(true);
  };

  const confirmDeleteSale = async () => {
    try {
      const response = await saleService.deleteSale(saleToDelete.id);
      
      if (response.success) {
        showNotification('Venda excluída com sucesso!');
        
        // Atualizar lista local imediatamente
        setSales(prev => prev.filter(s => s.id !== saleToDelete.id));
        
        // Recarregar dados da dashboard via AJAX em background
        setTimeout(() => {
          reloadDashboardData(false);
        }, 1000);
      } else {
        throw new Error(response.message || 'Erro ao excluir venda');
      }
    } catch (error) {
      console.error('❌ Erro ao excluir venda:', error);
      showNotification('Erro ao excluir venda: ' + error.message, 'error');
    } finally {
      setShowDeleteSaleConfirm(false);
      setSaleToDelete(null);
    }
  };

  const handleUpdateSale = async (saleData) => {
    try {
      const response = await saleService.updateSale(editingSale.id, saleData);
      
      if (response.success) {
        showNotification('Venda atualizada com sucesso!');
        
        // Recarregar dados da dashboard via AJAX
        await reloadDashboardData(false);
      } else {
        throw new Error(response.message || 'Erro ao atualizar venda');
      }
    } catch (error) {
      console.error('Erro ao atualizar venda:', error);
      throw error;
    }
  };

  const handleCloseSaleModal = () => {
    setShowSaleModal(false);
    setEditingSale(null);
  };

  // Função para mostrar notificação
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  // Verificar se há dados para renderizar
  const hasData = vehicles.length > 0 || customers.length > 0 || sales.length > 0;
  
  // Se estiver carregando e não há dados, mostrar loading
  if (loading && !hasData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando dashboard...</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
            Se esta tela persistir, verifique o console do navegador (F12)
          </p>
          <button
            onClick={() => {
              setLoading(false);
              reloadDashboardData(false);
            }}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            🔄 Forçar Recarregamento
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <header className="bg-white dark:bg-dark-800 shadow-sm border-b border-gray-200 dark:border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center py-3">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center">
                <Car className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Dashboard - Avenida Veículos
              </h1>
            </div>
            
            <div className="flex items-center justify-between sm:justify-end space-x-4">
              <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <Bell className="h-5 w-5" />
              </button>
              
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {user?.role}
                  </p>
                </div>
                <div className="h-8 w-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.name?.charAt(0)}
                  </span>
                </div>
                <button
                  onClick={() => {
                    logout();
                    navigate('/admin/login');
                  }}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  title="Sair"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
          <nav className="flex space-x-4 overflow-x-auto -mx-2 px-2">
            {[
              { id: 'overview', label: 'Visão Geral' },
              { id: 'vehicles', label: 'Veículos' },
              { id: 'sales', label: 'Vendas' },
              { id: 'customers', label: 'Clientes' },
                { id: 'reports', label: 'Relatórios' },
              { id: 'settings', label: 'Configurações' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {stat.title}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stat.value}
                      </p>
                      <p className={`text-sm ${
                        stat.changeType === 'positive' 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {stat.change} vs mês anterior
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${stat.color} text-white`}>
                      {stat.icon}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Atividade Recente
              </h3>
              <div className="space-y-4">
                {recentActivities.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Nenhuma atividade recente encontrada.
                  </p>
                ) : (
                  recentActivities.map((activity, index) => (
                    <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                      <div className="h-2 w-2 bg-primary rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 dark:text-white">
                          {activity.title}:{' '}
                          <span className="font-medium">{activity.detail}</span>
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTimeAgo(activity.date)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'vehicles' && (
          <div className="space-y-6">
            {/* Título da seção */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Gerenciar Veículos
              </h2>
            </div>
            {/* Vehicle Actions */}
            <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar veículos..."
                    className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <button className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 w-full md:w-auto">
                  <Filter className="h-4 w-4" />
                  <span>Filtros</span>
                </button>
              </div>
              <button 
                onClick={() => setShowVehicleModal(true)}
                className="w-full md:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Adicionar Veículo</span>
              </button>
            </div>

            {/* Vehicles Table */}
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
                  <thead className="bg-gray-50 dark:bg-dark-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Veículo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ano
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Quilometragem
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Preço
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ordem
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-700">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                        </td>
                      </tr>
                    ) : (
                      vehicles.map((vehicle) => (
                        <tr key={vehicle.id} className="hover:bg-gray-50 dark:hover:bg-dark-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-12 w-12 bg-gray-200 dark:bg-dark-600 rounded-lg flex items-center justify-center">
                                <Car className="h-6 w-6 text-gray-400" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {vehicle.brand} {vehicle.model}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {(vehicle.fuel_type || vehicle.fuel) } • {vehicle.transmission}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {vehicle.year}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {formatMileage(vehicle.mileage)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {formatPrice(vehicle.price)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(vehicle.status)}`}>
                              {getStatusText(vehicle.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            <div className="flex items-center space-x-2">
                              <span>{typeof vehicle.display_order === 'number' ? vehicle.display_order : 0}</span>
                              <div className="flex flex-col ml-1">
                                <button
                                  type="button"
                                  onClick={() => handleChangeDisplayOrder(vehicle, 'up')}
                                  className="p-0.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                                  title="Subir"
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleChangeDisplayOrder(vehicle, 'down')}
                                  className="p-0.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                                  title="Descer"
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => handleViewVehicle(vehicle)}
                                className="text-primary hover:text-primary-600 p-1 rounded hover:bg-primary/10 transition-colors"
                                title="Visualizar"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => handleEditVehicle(vehicle)}
                                className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => handleDeleteVehicle(vehicle)}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Excluir"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sales' && (
          <div className="space-y-6">
            <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Gerenciar Vendas
              </h2>
              <div className="flex flex-col sm:flex-row gap-3 sm:space-x-4">
                <button className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 w-full sm:w-auto">
                  <Filter className="h-4 w-4" />
                  <span>Filtros</span>
                </button>
                <button 
                  onClick={() => setShowSaleModal(true)}
                  className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4" />
                  <span>Registrar Venda</span>
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
                  <thead className="bg-gray-50 dark:bg-dark-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Venda
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-700">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                        </td>
                      </tr>
                    ) : (
                      sales.map((sale) => (
                        <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-dark-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-12 w-12 bg-gray-200 dark:bg-dark-600 rounded-lg flex items-center justify-center">
                                <Car className="h-6 w-6 text-gray-400" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {sale.vehicle?.brand} {sale.vehicle?.model}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {sale.vehicle?.year}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {sale.customer?.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {sale.customer?.phone}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {formatPrice(sale.sale_price)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {new Date(sale.sale_date).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sale.status)}`}>
                              {getStatusText(sale.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => handleViewSale(sale)}
                                className="text-primary hover:text-primary-600 p-1 rounded hover:bg-primary/10 transition-colors"
                                title="Visualizar"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => handleEditSale(sale)}
                                className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => handleDeleteSale(sale)}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Excluir"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'customers' && (
          <div className="space-y-6">
            <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Gerenciar Clientes
              </h2>
              <div className="flex flex-col sm:flex-row gap-3 sm:space-x-4">
                <button className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 w-full sm:w-auto">
                  <Filter className="h-4 w-4" />
                  <span>Filtros</span>
                </button>
                <button 
                  onClick={() => setShowCustomerModal(true)}
                  className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4" />
                  <span>Cadastrar Cliente</span>
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
                  <thead className="bg-gray-50 dark:bg-dark-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Contato
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        CPF
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Cadastro
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-700">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                        </td>
                      </tr>
                    ) : (
                      customers.map((customer) => (
                        <tr key={customer.id} className="hover:bg-gray-50 dark:hover:bg-dark-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-12 w-12 bg-gray-200 dark:bg-dark-600 rounded-lg flex items-center justify-center">
                                <Users className="h-6 w-6 text-gray-400" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {customer.name}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {customer.email || 'Sem email'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {customer.phone}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {customer.cpf || 'Não informado'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {new Date(customer.created_at).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              customer.is_active 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {customer.is_active ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => handleViewCustomer(customer)}
                                className="text-primary hover:text-primary-600 p-1 rounded hover:bg-primary/10 transition-colors"
                                title="Visualizar"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => handleEditCustomer(customer)}
                                className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => handleDeleteCustomer(customer)}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Excluir"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Relatórios e Análises
              </h2>
            </div>
            
            <SalesChart sales={sales} />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Configurações da Conta
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nome
                    </label>
                    <input
                      type="text"
                      value={user?.name || ''}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cargo
                  </label>
                  <input
                    type="text"
                    value={user?.role || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-gray-50 dark:bg-dark-600 text-gray-500 dark:text-gray-400"
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-200 dark:border-dark-700">
                  <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors">
                    Salvar Alterações
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      logout();
                      navigate('/admin/login');
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors text-sm font-medium"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair da conta
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Cadastro/Edição de Veículo */}
      <VehicleModal
        isOpen={showVehicleModal}
        onClose={handleCloseVehicleModal}
        onSubmit={editingVehicle ? handleUpdateVehicle : handleVehicleSubmit}
        editingVehicle={editingVehicle}
      />

      {/* Modal de Cadastro/Edição de Cliente */}
      <CustomerModal
        isOpen={showCustomerModal}
        onClose={handleCloseCustomerModal}
        onSubmit={editingCustomer ? handleUpdateCustomer : handleCustomerSubmit}
        editingCustomer={editingCustomer}
      />

      {/* Modal de Registro/Edição de Venda */}
      <SaleModal
        isOpen={showSaleModal}
        onClose={handleCloseSaleModal}
        onSubmit={editingSale ? handleUpdateSale : handleSaleSubmit}
        editingSale={editingSale}
        customers={customers}
        vehicles={vehicles}
      />

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Confirmar Exclusão
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Esta ação não pode ser desfeita
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300">
                Tem certeza que deseja excluir o veículo{' '}
                <span className="font-semibold">
                  {vehicleToDelete?.brand} {vehicleToDelete?.model}
                </span>?
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setVehicleToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteVehicle}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão de Cliente */}
      {showDeleteCustomerConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Confirmar Exclusão
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Esta ação não pode ser desfeita
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300">
                Tem certeza que deseja excluir o cliente{' '}
                <span className="font-semibold">
                  {customerToDelete?.name}
                </span>?
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteCustomerConfirm(false);
                  setCustomerToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteCustomer}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão de Venda */}
      {showDeleteSaleConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Confirmar Exclusão
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Esta ação não pode ser desfeita
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300">
                Tem certeza que deseja excluir a venda do veículo{' '}
                <span className="font-semibold">
                  {saleToDelete?.vehicle?.brand} {saleToDelete?.vehicle?.model}
                </span>?
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteSaleConfirm(false);
                  setSaleToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteSale}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Veículo */}
      {viewingVehicle && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Car className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {viewingVehicle.brand} {viewingVehicle.model}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Ano {viewingVehicle.year}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewingVehicle(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informações Básicas */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Informações Básicas
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Marca:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingVehicle.brand}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Modelo:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingVehicle.model}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Ano:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingVehicle.year}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Quilometragem:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatMileage(viewingVehicle.mileage)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Preço:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(viewingVehicle.price)}</span>
                    </div>
                  </div>
                </div>

                {/* Especificações */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Especificações
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Combustível:</span>
                      <span className="font-medium text-gray-900 dark:text-white capitalize">{viewingVehicle.fuel_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Câmbio:</span>
                      <span className="font-medium text-gray-900 dark:text-white capitalize">{viewingVehicle.transmission}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Cor:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingVehicle.color}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(viewingVehicle.status)}`}>
                        {getStatusText(viewingVehicle.status)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Descrição */}
              {viewingVehicle.description && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Descrição
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {viewingVehicle.description}
                  </p>
                </div>
              )}

              {/* Características */}
              {viewingVehicle.features && viewingVehicle.features.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Características
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {viewingVehicle.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Check className="h-4 w-4 text-green-500" />
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notificação */}
      {notification && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`p-4 rounded-lg shadow-lg flex items-center space-x-3 ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <Check className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
            </div>
            <span className="font-medium">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="flex-shrink-0 ml-2 text-white hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Cliente */}
      {viewingCustomer && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {viewingCustomer.name}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Cliente desde {new Date(viewingCustomer.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewingCustomer(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informações Pessoais */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Informações Pessoais
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Nome:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingCustomer.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Email:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingCustomer.email || 'Não informado'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Telefone:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingCustomer.phone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">CPF:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingCustomer.cpf || 'Não informado'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Data de Nascimento:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {viewingCustomer.birth_date ? new Date(viewingCustomer.birth_date).toLocaleDateString('pt-BR') : 'Não informado'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Endereço */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Endereço
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">CEP:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingCustomer.zip_code || 'Não informado'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Endereço:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingCustomer.address || 'Não informado'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Cidade:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingCustomer.city || 'Não informado'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Estado:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingCustomer.state || 'Não informado'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        viewingCustomer.is_active 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {viewingCustomer.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Observações */}
              {viewingCustomer.notes && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Observações
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {viewingCustomer.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Venda */}
      {viewingSale && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Venda #{viewingSale.id}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {new Date(viewingSale.sale_date).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewingSale(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Veículo e Cliente */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Veículo e Cliente
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400 block">Veículo:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {viewingSale.vehicle?.brand} {viewingSale.vehicle?.model} {viewingSale.vehicle?.year}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400 block">Cliente:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{viewingSale.customer?.name}</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{viewingSale.customer?.phone}</p>
                    </div>
                  </div>
                </div>

                {/* Valores */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Valores
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Preço de Venda:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(viewingSale.sale_price)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Entrada:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(viewingSale.down_payment || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Valor Financiado:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(viewingSale.financing_amount || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Comissão:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(viewingSale.commission || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalhes da Venda */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Detalhes da Venda
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Método de Pagamento:</span>
                      <span className="font-medium text-gray-900 dark:text-white capitalize">{viewingSale.payment_method}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(viewingSale.status)}`}>
                        {getStatusText(viewingSale.status)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Data de Entrega:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {viewingSale.delivery_date ? new Date(viewingSale.delivery_date).toLocaleDateString('pt-BR') : 'Não agendada'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Financiamento */}
                {viewingSale.payment_method === 'financing' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Financiamento
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Prazo:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{viewingSale.financing_term} meses</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Taxa de Juros:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{viewingSale.interest_rate}% a.a.</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Parcela Mensal:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formatPrice(viewingSale.monthly_payment || 0)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Observações */}
              {viewingSale.notes && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Observações
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {viewingSale.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};
